import React, { useEffect, useState } from 'react';
import '../styles/ContactPage.css';

/**
 * Contact Us — surfaces VIN Wholesale's contact channels (the same phone /
 * email / hours used across the app) and a message form. There is no contact
 * API, so the form composes a pre-filled mailto: to info@vindistro.com.
 */
const CONTACT_EMAIL = 'info@vindistro.com';
const CONTACT_PHONE = '5028074201';

const CHANNELS = [
  { icon: '📞', label: 'Call us', value: CONTACT_PHONE, href: `tel:${CONTACT_PHONE}` },
  { icon: '✉️', label: 'Email us', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
  { icon: '🕐', label: 'Hours', value: 'Mon – Sat · 7 AM – 8 PM', href: null },
];

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', business: '', email: '', phone: '', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const update = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Website enquiry from ${form.name || 'a customer'}`;
    const body = [
      `Name: ${form.name}`,
      `Business: ${form.business}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      '',
      form.message,
    ].join('\n');
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <span className="contact-hero__tag">CONTACT</span>
        <h1 className="contact-hero__title">We're here when it matters</h1>
        <p className="contact-hero__subtitle">
          Questions about an order, becoming a partner, or anything else? Reach
          our team directly — every query is handled by a real person, usually
          within one business day.
        </p>
      </section>

      <div className="contact-grid">
        {/* Channels */}
        <aside className="contact-channels">
          {CHANNELS.map((c) => (
            <div key={c.label} className="contact-channel">
              <span className="contact-channel__icon">{c.icon}</span>
              <div className="contact-channel__body">
                <span className="contact-channel__label">{c.label}</span>
                {c.href ? (
                  <a href={c.href} className="contact-channel__value contact-channel__value--link">
                    {c.value}
                  </a>
                ) : (
                  <span className="contact-channel__value">{c.value}</span>
                )}
              </div>
            </div>
          ))}
        </aside>

        {/* Form */}
        <form className="contact-form" onSubmit={handleSubmit}>
          <h2 className="contact-form__title">Send us a message</h2>
          <div className="contact-form__row">
            <label className="contact-field">
              <span>Name</span>
              <input type="text" value={form.name} onChange={update('name')} required placeholder="Your name" />
            </label>
            <label className="contact-field">
              <span>Business name</span>
              <input type="text" value={form.business} onChange={update('business')} placeholder="Store / business" />
            </label>
          </div>
          <div className="contact-form__row">
            <label className="contact-field">
              <span>Email</span>
              <input type="email" value={form.email} onChange={update('email')} required placeholder="you@example.com" />
            </label>
            <label className="contact-field">
              <span>Phone</span>
              <input type="tel" value={form.phone} onChange={update('phone')} placeholder="Optional" />
            </label>
          </div>
          <label className="contact-field">
            <span>Message</span>
            <textarea value={form.message} onChange={update('message')} required rows={5} placeholder="How can we help?" />
          </label>
          <button type="submit" className="contact-form__submit">Send Message</button>
          <p className="contact-form__note">
            This opens your email app pre-addressed to {CONTACT_EMAIL}.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactPage;
